import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { db } from '../Firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, X, Check, Loader2, 
  PlusCircle, MinusCircle, MapPin, Calendar, User, 
  Activity, ClipboardList, Search
} from 'lucide-react';

export default function ManageProjects() {
  const [activeTab, setActiveTab] = useState('completed'); // 'completed' or 'upcoming'
  const [completedList, setCompletedList] = useState([]);
  const [upcomingList, setUpcomingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modals
  const [completedModalOpen, setCompletedModalOpen] = useState(false);
  const [upcomingModalOpen, setUpcomingModalOpen] = useState(false);
  const [currentCompleted, setCurrentCompleted] = useState(null); // null for new
  const [currentUpcoming, setCurrentUpcoming] = useState(null); // null for new

  // Completed Project Form Fields
  const [compTitle, setCompTitle] = useState('');
  const [compLocation, setCompLocation] = useState('');
  const [compAuthority, setCompAuthority] = useState('');
  const [compTimeline, setCompTimeline] = useState('');
  const [compDesc, setCompDesc] = useState('');
  const [compImage, setCompImage] = useState('');
  const [compSupplies, setCompSupplies] = useState(['']);

  // Upcoming Project Form Fields
  const [upNum, setUpNum] = useState('01');
  const [upHosp, setUpHosp] = useState('');
  const [upLoc, setUpLoc] = useState('');
  const [upScope, setUpScope] = useState('');

  // Search/Filters
  const [searchTerm, setSearchTerm] = useState('');

  // No error state

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch Completed Projects
      const compSnap = await getDocs(collection(db, 'completed_projects'));
      let compData = compSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCompletedList(compData);

      // Fetch Upcoming Projects
      const upSnap = await getDocs(collection(db, 'upcoming_projects'));
      let upData = upSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      upData.sort((a, b) => (a.num || '').localeCompare(b.num || ''));
      setUpcomingList(upData);
    } catch (err) {
      console.error("Error fetching projects data:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to fetch projects data.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Completed Projects CRUD helpers
  const openAddCompleted = () => {
    setCurrentCompleted(null);
    setCompTitle('');
    setCompLocation('');
    setCompAuthority('');
    setCompTimeline('');
    setCompDesc('');
    setCompImage('');
    setCompSupplies(['']);
    setCompletedModalOpen(true);
  };

  const openEditCompleted = (proj) => {
    setCurrentCompleted(proj);
    setCompTitle(proj.title || '');
    setCompLocation(proj.location || '');
    setCompAuthority(proj.authority || '');
    setCompTimeline(proj.timeline || '');
    setCompDesc(proj.desc || '');
    setCompImage(proj.image || '');
    setCompSupplies(proj.supplies && proj.supplies.length > 0 ? [...proj.supplies] : ['']);
    setCompletedModalOpen(true);
  };

  const handleAddSupplyField = () => {
    setCompSupplies([...compSupplies, '']);
  };

  const handleRemoveSupplyField = (idx) => {
    if (compSupplies.length === 1) return;
    const updated = [...compSupplies];
    updated.splice(idx, 1);
    setCompSupplies(updated);
  };

  const handleSupplyChange = (idx, val) => {
    const updated = [...compSupplies];
    updated[idx] = val;
    setCompSupplies(updated);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 819200) {
      Swal.fire({
        title: 'Image Too Large',
        text: 'The uploaded image exceeds the 800 KB limit. Please upload a smaller image.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCompImage(reader.result);
    };
    reader.onerror = (err) => {
      console.error('Image reading error:', err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to process image file.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setCompImage('');
  };

  const handleCompletedSubmit = async (e) => {
    e.preventDefault();

    if (!compTitle || !compLocation || !compAuthority || !compTimeline || !compDesc || !compImage || compImage === '/assets/logo1.png') {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'All fields, including Project Image, Authority, and Timeline Label, are required.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    // Check for duplicate title
    const isDuplicate = completedList.some(p => 
      p.title.trim().toLowerCase() === compTitle.trim().toLowerCase() && 
      (!currentCompleted || p.id !== currentCompleted.id)
    );
    if (isDuplicate) {
      Swal.fire({
        title: 'Duplicate Title',
        text: 'A completed project with this title/hospital name already exists.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    const filteredSupplies = compSupplies.map(s => s.trim()).filter(s => s !== '');
    if (filteredSupplies.length === 0) {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'Please add at least one supply/installed item.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    const projectData = {
      title: compTitle.trim(),
      location: compLocation.trim(),
      authority: compAuthority.trim(),
      timeline: compTimeline.trim(),
      desc: compDesc.trim(),
      image: compImage,
      supplies: filteredSupplies,
      updatedAt: new Date()
    };

    try {
      setSaving(true);
      if (currentCompleted) {
        const docRef = doc(db, 'completed_projects', currentCompleted.id);
        await updateDoc(docRef, projectData);
        Swal.fire({
          title: 'Success',
          text: 'Completed project updated successfully!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      } else {
        projectData.createdAt = new Date();
        await addDoc(collection(db, 'completed_projects'), projectData);
        Swal.fire({
          title: 'Success',
          text: 'Completed project created successfully!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      }
      await fetchData();
      setTimeout(() => setCompletedModalOpen(false), 1000);
    } catch (err) {
      console.error("Error saving completed project:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to save completed project.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCompletedDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111111',
      cancelButtonColor: '#DE2929',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'completed_projects', id));
      setCompletedList(completedList.filter(p => p.id !== id));
      Swal.fire({
        title: 'Success',
        text: 'Completed project deleted successfully!',
        icon: 'success',
        confirmButtonColor: '#111111'
      });
    } catch (err) {
      console.error("Error deleting completed project:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete completed project.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  // Upcoming Projects CRUD helpers
  const openAddUpcoming = () => {
    setCurrentUpcoming(null);
    // Find next sequence number
    let nextNum = '01';
    if (upcomingList.length > 0) {
      const nums = upcomingList.map(u => parseInt(u.num || '0', 10)).filter(n => !isNaN(n));
      if (nums.length > 0) {
        const max = Math.max(...nums);
        nextNum = String(max + 1).padStart(2, '0');
      }
    }
    setUpNum(nextNum);
    setUpHosp('');
    setUpLoc('');
    setUpScope('');
    setUpcomingModalOpen(true);
  };

  const openEditUpcoming = (proj) => {
    setCurrentUpcoming(proj);
    setUpNum(proj.num || '01');
    setUpHosp(proj.hosp || '');
    setUpLoc(proj.loc || '');
    setUpScope(proj.scope || '');
    setUpcomingModalOpen(true);
  };

  const handleUpcomingSubmit = async (e) => {
    e.preventDefault();

    if (!upHosp || !upLoc || !upScope) {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'Hospital Name, Location, and Scope details are required.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    // Check for duplicate hospital name
    const isDuplicate = upcomingList.some(p => 
      p.hosp.trim().toLowerCase() === upHosp.trim().toLowerCase() && 
      (!currentUpcoming || p.id !== currentUpcoming.id)
    );
    if (isDuplicate) {
      Swal.fire({
        title: 'Duplicate Project',
        text: 'An upcoming project with this hospital name already exists.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    const projectData = {
      num: upNum.trim() || '01',
      hosp: upHosp.trim(),
      loc: upLoc.trim(),
      scope: upScope.trim(),
      updatedAt: new Date()
    };

    try {
      setSaving(true);
      if (currentUpcoming) {
        const docRef = doc(db, 'upcoming_projects', currentUpcoming.id);
        await updateDoc(docRef, projectData);
        Swal.fire({
          title: 'Success',
          text: 'Upcoming project updated successfully!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      } else {
        projectData.createdAt = new Date();
        await addDoc(collection(db, 'upcoming_projects'), projectData);
        Swal.fire({
          title: 'Success',
          text: 'Upcoming project created successfully!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      }
      await fetchData();
      setTimeout(() => setUpcomingModalOpen(false), 1000);
    } catch (err) {
      console.error("Error saving upcoming project:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to save upcoming project.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpcomingDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111111',
      cancelButtonColor: '#DE2929',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'upcoming_projects', id));
      setUpcomingList(upcomingList.filter(p => p.id !== id));
      Swal.fire({
        title: 'Success',
        text: 'Upcoming project deleted successfully!',
        icon: 'success',
        confirmButtonColor: '#111111'
      });
    } catch (err) {
      console.error("Error deleting upcoming project:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete upcoming project.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filters
  const filteredCompleted = completedList.filter(p => 
    p.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.location?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.desc?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUpcoming = upcomingList.filter(p => 
    p.hosp?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.loc?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.scope?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 font-poppins">
      
      {/* Upper Panel Page Info */}
      <div className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-extrabold font-montserrat text-brand-navy">Manage Projects</h1>
          <p className="text-xs text-gray-500">Add, edit, or remove completed clinical showcases and upcoming project roadmaps.</p>
        </div>
        <button 
          onClick={activeTab === 'completed' ? openAddCompleted : openAddUpcoming}
          className="btn-primary py-2.5 px-5 text-sm shrink-0 self-start sm:self-center"
        >
          <Plus size={16} />
          {activeTab === 'completed' ? 'Add Completed Project' : 'Add Upcoming Project'}
        </button>
      </div>

      {/* Toggle Tab Options */}
      <div className="flex bg-white p-1 rounded-2xl border border-brand-silver/50 shadow-sm max-w-md">
        <button
          onClick={() => { setActiveTab('completed'); setSearchTerm(''); }}
          className={`flex-grow flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold font-poppins transition-all duration-200 ${
            activeTab === 'completed'
              ? 'bg-brand-navy text-white shadow-sm'
              : 'text-gray-400 hover:text-brand-navy hover:bg-brand-light'
          }`}
        >
          <Check size={14} />
          Completed Projects ({completedList.length})
        </button>
        <button
          onClick={() => { setActiveTab('upcoming'); setSearchTerm(''); }}
          className={`flex-grow flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold font-poppins transition-all duration-200 ${
            activeTab === 'upcoming'
              ? 'bg-brand-navy text-white shadow-sm'
              : 'text-gray-400 hover:text-brand-navy hover:bg-brand-light'
          }`}
        >
          <Activity size={14} />
          Upcoming Projects ({upcomingList.length})
        </button>
      </div>



      {/* Search Toolbar */}
      <div className="bg-white p-4 rounded-2xl border border-brand-silver/50 shadow-sm flex items-center justify-between">
        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder={activeTab === 'completed' ? "Search by title, location, description..." : "Search by hospital, location, scope..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
          />
          <Search size={14} className="absolute left-3.5 top-3.5 text-gray-400" />
        </div>
      </div>

      {/* Tab Content Display */}
      <div>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 space-y-4">
            <Loader2 className="animate-spin text-brand-blue" size={36} />
            <p className="text-xs font-bold text-gray-500">Loading projects log...</p>
          </div>
        ) : activeTab === 'completed' ? (
          /* Completed Projects Section */
          filteredCompleted.length === 0 ? (
            <div className="bg-white rounded-3xl border border-brand-silver/50 p-12 text-center text-gray-400 max-w-md mx-auto space-y-4">
              <ClipboardList size={48} className="mx-auto text-gray-300" />
              <h3 className="text-lg font-bold font-montserrat text-brand-navy">No Completed Projects</h3>
              <p className="text-xs font-light text-gray-500">No projects log entries match search parameter. Click "Add Completed Project" to begin.</p>
              <button onClick={openAddCompleted} className="btn-primary py-2 px-4 text-xs mx-auto">Add Completed Project</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredCompleted.map((proj) => (
                <motion.div
                  key={proj.id}
                  layout
                  className="bg-white rounded-2xl border border-brand-silver/60 shadow-sm overflow-hidden flex flex-col justify-between hover:shadow-md transition-all group"
                >
                  <div className="h-40 bg-gradient-to-br from-brand-navy to-brand-blue p-4 flex flex-col justify-between relative overflow-hidden">
                    {proj.image && !proj.image.startsWith('/assets/logo1.png') ? (
                      <>
                        <img src={proj.image} alt={proj.title} className="absolute inset-0 w-full h-full object-cover z-0" />
                        <div className="absolute inset-0 bg-black/40 z-0" />
                      </>
                    ) : (
                      <div className="absolute inset-0 bg-grid-mesh opacity-10 z-0" />
                    )}
                    <div className="flex justify-between items-start relative z-10">
                      <span className="bg-brand-navy/70 text-brand-green text-[9px] uppercase font-bold tracking-widest px-2.5 py-0.5 rounded border border-white/5 flex items-center gap-1">
                        <Calendar size={10} /> {proj.timeline || 'Completed'}
                      </span>
                      <div className="flex gap-2 bg-white/10 rounded-lg p-0.5 border border-white/5 opacity-80 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={() => openEditCompleted(proj)}
                          className="p-1 rounded text-white hover:text-brand-blue hover:bg-white transition-colors"
                          title="Edit Project"
                        >
                          <Edit2 size={12} />
                        </button>
                        <button 
                          onClick={() => handleCompletedDelete(proj.id)}
                          className="p-1 rounded text-white hover:text-red-500 hover:bg-white transition-colors"
                          title="Delete Project"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="relative z-10">
                      <h4 className="text-white font-extrabold text-sm font-montserrat line-clamp-1 leading-snug">{proj.title}</h4>
                      <p className="text-[10px] text-brand-silver font-semibold flex items-center gap-0.5 mt-0.5"><MapPin size={10} /> {proj.location}</p>
                    </div>
                  </div>

                  <div className="p-5 flex-grow flex flex-col justify-between space-y-4">
                    <div>
                      <p className="text-[10px] text-brand-blue font-bold font-poppins uppercase leading-none mb-1.5">{proj.authority}</p>
                      <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">{proj.desc}</p>
                    </div>
                    
                    <div className="space-y-1.5 pt-2 border-t border-brand-silver/50">
                      <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins flex items-center gap-1.5"><ClipboardList size={12} /> supplies ({proj.supplies?.length || 0})</span>
                      <ul className="text-[10px] text-gray-600 font-poppins space-y-0.5 pl-1 list-disc list-inside">
                        {proj.supplies?.slice(0, 3).map((item, sIdx) => (
                          <li key={sIdx} className="truncate">{item}</li>
                        ))}
                        {proj.supplies?.length > 3 && (
                          <li className="text-brand-blue font-bold list-none">+ {proj.supplies.length - 3} more items</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )
        ) : (
          /* Upcoming Projects Section */
          filteredUpcoming.length === 0 ? (
            <div className="bg-white rounded-3xl border border-brand-silver/50 p-12 text-center text-gray-400 max-w-md mx-auto space-y-4">
              <Activity size={48} className="mx-auto text-gray-300" />
              <h3 className="text-lg font-bold font-montserrat text-brand-navy">No Upcoming Projects</h3>
              <p className="text-xs font-light text-gray-500">No upcoming project timeline logs match search criteria. Click "Add Upcoming Project" to begin.</p>
              <button onClick={openAddUpcoming} className="btn-primary py-2 px-4 text-xs mx-auto">Add Upcoming Project</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredUpcoming.map((proj) => (
                <motion.div
                  key={proj.id}
                  layout
                  className="bg-white p-5 rounded-2xl border border-brand-silver bg-brand-light flex flex-col justify-between hover:border-brand-blue/30 transition-all duration-200 group"
                >
                  <div className="space-y-3">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex items-center gap-2">
                        <span className="w-8 h-8 rounded-full bg-brand-navy text-brand-green flex items-center justify-center font-extrabold text-xs shadow-sm border border-brand-silver/40">
                          {proj.num}
                        </span>
                        <div>
                          <h4 className="text-xs font-bold text-brand-navy font-montserrat">{proj.hosp}</h4>
                          <span className="text-[9px] text-brand-blue font-bold font-poppins uppercase tracking-wider block mt-0.5">{proj.loc}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                        <button 
                          onClick={() => openEditUpcoming(proj)}
                          className="p-1 rounded bg-white border border-brand-silver text-gray-500 hover:text-brand-blue hover:border-brand-blue transition-colors"
                          title="Edit Upcoming Project"
                        >
                          <Edit2 size={12} />
                        </button>
                        <button 
                          onClick={() => handleUpcomingDelete(proj.id)}
                          className="p-1 rounded bg-white border border-brand-silver text-gray-500 hover:text-red-500 hover:border-red-500 transition-colors"
                          title="Delete Upcoming Project"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>

                    <p className="text-[11px] text-gray-500 font-poppins leading-relaxed line-clamp-3 pt-2 border-t border-brand-silver/50">{proj.scope}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Completed Project Form Modal */}
      <AnimatePresence>
        {completedModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => !saving && setCompletedModalOpen(false)}
              className="absolute inset-0 bg-black"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 relative z-10 flex flex-col max-h-[90vh]"
            >
              <div className="p-6 border-b border-brand-silver/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-montserrat text-brand-navy">
                    {currentCompleted ? 'Edit Completed Project' : 'Add Completed Project'}
                  </h2>
                  <p className="text-xs text-gray-500">Provide details for completed facility showcasing.</p>
                </div>
                <button 
                  onClick={() => setCompletedModalOpen(false)}
                  disabled={saving}
                  className="p-1.5 rounded-lg hover:bg-brand-light text-gray-400 hover:text-brand-navy transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleCompletedSubmit} className="flex-grow overflow-y-auto p-6 space-y-6">

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Title */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Project Title / Hospital Name *</label>
                    <input 
                      type="text"
                      required
                      placeholder="Enter project title or hospital name"
                      value={compTitle}
                      onChange={(e) => setCompTitle(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>

                  {/* Location */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Location (City) *</label>
                    <input 
                      type="text"
                      required
                      placeholder="Enter city location"
                      value={compLocation}
                      onChange={(e) => setCompLocation(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Authority */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Authority / Chief Doctor *</label>
                    <input 
                      type="text"
                      required
                      placeholder="Enter authority or chief doctor's name"
                      value={compAuthority}
                      onChange={(e) => setCompAuthority(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>

                  {/* Timeline */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Timeline Label *</label>
                    <input 
                      type="text"
                      required
                      placeholder="Enter timeline (e.g. November 2025)"
                      value={compTimeline}
                      onChange={(e) => setCompTimeline(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>
                </div>

                {/* Completed Project Image Upload */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Project Image * (Up to 800 KB)</label>
                  {!compImage || compImage === '/assets/logo1.png' ? (
                    <div className="relative border-2 border-dashed border-brand-silver hover:border-brand-blue rounded-xl p-6 flex flex-col items-center justify-center bg-brand-light/35 transition-colors cursor-pointer">
                      <input 
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      />
                      <div className="text-center space-y-1.5">
                        <PlusCircle className="mx-auto text-gray-400" size={24} />
                        <p className="text-xs text-gray-500 font-poppins font-semibold text-brand-blue">Click to upload image</p>
                        <p className="text-[10px] text-gray-400 font-poppins">PNG, JPG or WEBP (Max 800 KB)</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 rounded-xl border border-brand-blue/20 bg-brand-blue/5 shadow-inner">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-12 rounded-lg overflow-hidden border border-brand-silver shrink-0 bg-white">
                          <img src={compImage} alt="Project preview" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-brand-navy font-poppins">Image uploaded successfully</p>
                          <p className="text-[10px] text-gray-500 font-poppins">Size is under 800 KB limit</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold font-poppins transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Project Overview / Description *</label>
                  <textarea 
                    rows={3}
                    required
                    placeholder="Enter project description"
                    value={compDesc}
                    onChange={(e) => setCompDesc(e.target.value)}
                    className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins resize-none leading-relaxed"
                  />
                </div>

                {/* Supplies list */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Installed Supplies & Matrix *</label>
                    <button 
                      type="button" 
                      onClick={handleAddSupplyField}
                      className="text-xs text-brand-blue hover:text-brand-navy flex items-center gap-1 font-bold"
                    >
                      <PlusCircle size={14} /> Add Item
                    </button>
                  </div>

                  <div className="space-y-2">
                    {compSupplies.map((item, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <span className="text-xs font-bold text-gray-400 shrink-0 w-4">{idx + 1}.</span>
                        <input 
                          type="text"
                          required
                          placeholder="Enter supply or matrix item details"
                          value={item}
                          onChange={(e) => handleSupplyChange(idx, e.target.value)}
                          className="flex-grow px-4 py-2 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                        />
                        <button 
                          type="button"
                          disabled={compSupplies.length === 1}
                          onClick={() => handleRemoveSupplyField(idx)}
                          className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
                        >
                          <MinusCircle size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submit Container */}
                <div className="pt-6 border-t border-brand-silver/50 flex justify-end gap-3">
                  <button 
                    type="button"
                    disabled={saving}
                    onClick={() => setCompletedModalOpen(false)}
                    className="px-5 py-2.5 border border-brand-silver hover:bg-brand-light text-brand-navy text-xs font-bold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={saving}
                    className="btn-primary py-2.5 px-6 text-xs flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin" size={14} />
                        Saving project...
                      </>
                    ) : (
                      <>
                        <Check size={14} />
                        {currentCompleted ? 'Save Changes' : 'Create Project'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Upcoming Project Form Modal */}
      <AnimatePresence>
        {upcomingModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => !saving && setUpcomingModalOpen(false)}
              className="absolute inset-0 bg-black"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white w-full max-w-xl rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 relative z-10 flex flex-col"
            >
              <div className="p-6 border-b border-brand-silver/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-montserrat text-brand-navy">
                    {currentUpcoming ? 'Edit Upcoming Project' : 'Add Upcoming Project'}
                  </h2>
                  <p className="text-xs text-gray-500">Provide details for upcoming project roadmap item.</p>
                </div>
                <button 
                  onClick={() => setUpcomingModalOpen(false)}
                  disabled={saving}
                  className="p-1.5 rounded-lg hover:bg-brand-light text-gray-400 hover:text-brand-navy transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleUpcomingSubmit} className="p-6 space-y-6">

                <div className="grid grid-cols-3 gap-4">
                  {/* Sequence Number */}
                  <div className="space-y-1.5 col-span-1">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Seq. Num *</label>
                    <input 
                      type="text"
                      required
                      placeholder="01"
                      value={upNum}
                      onChange={(e) => setUpNum(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins text-center"
                    />
                  </div>

                  {/* Hospital Name */}
                  <div className="space-y-1.5 col-span-2">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Hospital Name *</label>
                    <input 
                      type="text"
                      required
                      placeholder="Enter hospital name"
                      value={upHosp}
                      onChange={(e) => setUpHosp(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Location (City / Region) *</label>
                  <input 
                    type="text"
                    required
                    placeholder="Enter location"
                    value={upLoc}
                    onChange={(e) => setUpLoc(e.target.value)}
                    className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                  />
                </div>

                {/* Scope of Work */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Scope of Work *</label>
                  <textarea 
                    rows={4}
                    required
                    placeholder="Enter scope of work"
                    value={upScope}
                    onChange={(e) => setUpScope(e.target.value)}
                    className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins resize-none leading-relaxed"
                  />
                </div>

                {/* Submit Container */}
                <div className="pt-6 border-t border-brand-silver/50 flex justify-end gap-3">
                  <button 
                    type="button"
                    disabled={saving}
                    onClick={() => setUpcomingModalOpen(false)}
                    className="px-5 py-2.5 border border-brand-silver hover:bg-brand-light text-brand-navy text-xs font-bold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={saving}
                    className="btn-primary py-2.5 px-6 text-xs flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin" size={14} />
                        Saving upcoming...
                      </>
                    ) : (
                      <>
                        <Check size={14} />
                        {currentUpcoming ? 'Save Changes' : 'Add Upcoming'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
